import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProvider } from '../contexts/ProviderContext';
import { useToast } from '../contexts/ToastContext';
import { stripePromise } from '../lib/stripe';
import { publicPlans, sectionsFor } from '../lib/plans';
import { PlanCard, PlanClosingCta, PlanIntro, PlanListSections, gridColsFor } from './PlanOffer';
import { usePlans } from '../hooks/usePlans';
import { supabase } from '../lib/supabase';

const ProviderPaymentPlans: React.FC = () => {
  const { user } = useAuth();
  const { currentProviderId } = useProvider();
  const toast = useToast();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSelectPlan = async (planId: string) => {
    setSelectedPlan(planId);
    setLoading(true);
    try {
      if (!currentProviderId) throw new Error('No provider selected.');
      const { data, error } = await supabase.functions.invoke('stripe', {
        body: {
          action: 'create-checkout-session',
          planId,
          userEmail: user?.email,
          providerId: currentProviderId,
          successUrl: `${window.location.origin}/provider/catalog`,
          cancelUrl: `${window.location.origin}/provider/payment-plans`,
        },
      });
      if (error) throw error;
      const stripe = await stripePromise;
      if (!stripe) throw new Error('Stripe not loaded');
      const { error: stripeError } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
      if (stripeError) throw stripeError;
    } catch (err: any) {
      toast.error(err.message || 'Failed to start checkout process');
      setLoading(false);
    }
  };

  const { plans: allPlans, sections, loading: plansLoading, error: plansError } = usePlans();
  const plans = publicPlans(allPlans, 'provider');

  return (
    <div className="min-h-screen">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <PlanIntro section={sectionsFor(sections, 'provider', 'intro')[0]} fallbackHeading="Supplier plans">
          <span className="inline-block bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs px-4 py-1.5 rounded-full">
            Active subscription required to access provider features
          </span>
        </PlanIntro>

        {plansLoading && <div className="text-center text-white/40 text-sm mb-10">Loading plans…</div>}
        {!plansLoading && plans.length === 0 && (
          <div className="glass-card rounded-2xl p-6 text-center text-white/60 text-sm mb-10">
            {plansError ? 'Plans could not be loaded. Please refresh the page.' : 'No plans are available right now.'}
          </div>
        )}

        <div className={`grid grid-cols-1 ${gridColsFor(plans.length)} gap-6 mb-10`}>
          {plans.map((plan, i) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              index={i}
              busy={loading && selectedPlan === plan.id}
              onCheckout={handleSelectPlan}
            />
          ))}
        </div>

        <PlanListSections sections={sectionsFor(sections, 'provider', 'list')} />
        <PlanClosingCta section={sectionsFor(sections, 'provider', 'cta')[0]} />
      </main>
    </div>
  );
};

export default ProviderPaymentPlans;
